/**
 * SubscriptionSyncModal
 *
 * Admin-only modal to attach Stripe subscription/customer IDs and trigger a
 * sync for a user.
 */
import React, { useEffect, useState } from 'react';
import { X, CreditCard } from 'lucide-react';

export default function SubscriptionSyncModal({ isOpen, onClose, onSubmit, user, loading = false }) {
  const [subscriptionId, setSubscriptionId] = useState('');
  const [customerId, setCustomerId] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSubscriptionId(user?.stripeSubscriptionId || '');
      setCustomerId(user?.stripeCustomerId || '');
    }
  }, [isOpen, user]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;
    onSubmit({ subscriptionId: subscriptionId?.trim() || null, customerId: customerId?.trim() || null });
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Attach & Sync Stripe Subscription</h3>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-4 py-4 space-y-3">
          <div className="text-xs text-gray-600 dark:text-gray-400">
            <div><span className="font-medium">User:</span> {user?.email || user?.uid}</div>
            <div className="mt-1 text-gray-500 dark:text-gray-500">Provide either or both IDs. If left blank, the system will search Stripe by the saved customer.</div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Stripe Subscription ID (sub_...)</label>
            <input
              type="text"
              value={subscriptionId}
              onChange={(e) => setSubscriptionId(e.target.value)}
              placeholder="sub_..."
              className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Stripe Customer ID (cus_...)</label>
            <input
              type="text"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              placeholder="cus_..."
              className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="pt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="inline-flex items-center px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-3 py-2 text-xs font-medium text-primary-700 bg-primary-100 border border-primary-200 rounded-md hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-primary-900/20 dark:text-primary-400 dark:border-primary-800 disabled:opacity-60"
            >
              <CreditCard className="w-3 h-3 mr-1" />
              {loading ? 'Syncing...' : 'Sync Subscription'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 